import fetchService from '../servises/fetch.service';
import { makeAutoObservable } from 'mobx';
import utilsService from '../servises/utils.service';
import alarmsStore from './alarms.store';
import userStore from './user.store';
import { enqueueMutation } from '../servises/mutation-queue.service';
import {
    getAccountStateGeneration,
    isAccountStateCurrent,
    registerAccountStateReset
} from '../servises/account-state.service';

class UptimeStore {
    constructor() {
        makeAutoObservable(this, { tagMutationQueue: false });
    }

    uptimeListObj = {};
    uptimeCreated = null;
    canAddNewUptime = false;
    requestSequence = 0;
    tagMutationQueue = new Map();

    async getUptime(isForce) {
        if (!isForce && Object.keys(this.uptimeListObj).length > 0) {
            return;
        }
        const requestSequence = ++this.requestSequence;
        const response = await fetchService.authenticatedGet(`/uptime`);
        const responseData = response && response.data;
        if (!responseData || responseData.isError) {
            return;
        }
        const data = responseData.data_o === null ? [] : responseData.data_o;
        if (!Array.isArray(data)) {
            return;
        }
        if (requestSequence !== this.requestSequence) {
            return;
        }
        const incomingIds = new Set(data.map(item => item._id));
        Object.keys(this.uptimeListObj).forEach(uptimeId => {
            if (!incomingIds.has(uptimeId)) {
                alarmsStore.removeServerAlarms(uptimeId);
            }
        });
        data.forEach(item => {
            this.normalize(item);
        });
        this.uptimeListObj = utilsService.normalizeApiData(data);
        this.uptimeListObj = { ...this.uptimeListObj };
        this.detectIfCanAddNewServers();
    }

    async deleteMonitor(uptimeId) {
        const accountStateGeneration = getAccountStateGeneration();
        this.requestSequence += 1;
        await fetchService.authenticatedDelete(
            `/uptime/${ uptimeId }`);
        if (!isAccountStateCurrent(accountStateGeneration)) {
            return;
        }
        this.requestSequence += 1;
        delete this.uptimeListObj[uptimeId];
        this.uptimeListObj = { ...this.uptimeListObj };
        alarmsStore.removeServerAlarms(uptimeId);
        if (this.uptimeCreated && this.uptimeCreated._id === uptimeId) {
            this.uptimeCreated = null;
        }
        this.detectIfCanAddNewServers();
    }

    async updateUptime(id, uptime) {
        const accountStateGeneration = getAccountStateGeneration();
        this.requestSequence += 1;
        const response = await fetchService.authenticatedPut(`/uptime/${ id }`, uptime);
        if (!isAccountStateCurrent(accountStateGeneration)) {
            return;
        }
        this.requestSequence += 1;
        const data = response.data.data_o;
        if (data) {
            const existing = this.uptimeListObj[data._id] || {};
            const updated = { ...existing, ...data };
            this.normalize(updated);
            this.uptimeListObj[data._id] = updated;
            this.uptimeListObj = { ...this.uptimeListObj };
        }
    }

    async createUptime(payload) {
        const accountStateGeneration = getAccountStateGeneration();
        this.requestSequence += 1;
        const response = await fetchService.authenticatedPost(`/uptime`, payload);
        if (!isAccountStateCurrent(accountStateGeneration)) {
            return null;
        }
        this.requestSequence += 1;
        const responseData = response && response.data;
        const data = responseData && responseData.data_o;
        if (!responseData || responseData.isError || !data || !data._id) {
            throw new Error(responseData?.message || 'Failed to create uptime monitor');
        }
        this.normalize(data);
        this.uptimeListObj[data._id] = data;
        this.uptimeListObj = { ...this.uptimeListObj };
        this.uptimeCreated = data;
        this.detectIfCanAddNewServers();
        return data;
    }

    async updateUptimeTags(id, tagId, isAdd, isServerCreated) {
        const accountStateGeneration = getAccountStateGeneration();
        this.requestSequence += 1;
        return enqueueMutation(this.tagMutationQueue, id, async () => {
            if (!isAccountStateCurrent(accountStateGeneration)) {
                return;
            }
            const response = await fetchService.authenticatedPut(`/uptime/${ id }`,
                { tagId: tagId, isAdd }
            );
            if (!isAccountStateCurrent(accountStateGeneration)) {
                return;
            }
            this.requestSequence += 1;

            const data = response.data.data_o;
            this.uptimeListObj[data._id].tags = data.tags;
            this.uptimeListObj = { ...this.uptimeListObj };
        });
    }

    normalize(item) {
        item.classes = '';
        if (item.uptimechecks && item.uptimechecks.length) {
            item.uptimechecks = [...item.uptimechecks].sort(
                (first, second) => first.createdAt - second.createdAt
            );
            const check = item.uptimechecks[item.uptimechecks.length - 1];
            if (check.isValid) {
                item.classes = 'normal';
            } else {
                item.classes = 'old-data';
            }

            // item.timeAgo = utilsService.timeAgo(check.createdAt);

        } else {
            item.classes = 'no-data';
            // item.timeAgo = 'N/A';
        }
        if (!item.isEnabled) {
            item.classes = 'disabled';
        }
        alarmsStore.setAlarms(item);
    }

    detectIfCanAddNewServers() {
        const serversCount = Object.keys(this.uptimeListObj).length;
        const subscriptionServers = userStore.user.subscription.uptime;

        if (serversCount < subscriptionServers) {
            this.canAddNewUptime = true;
        } else {
            this.canAddNewUptime = false;
        }
    }

    clear() {
        this.requestSequence += 1;
        this.uptimeListObj = {};
        this.uptimeCreated = null;
        this.canAddNewUptime = false;
        this.tagMutationQueue.clear();
    }
}

const uptimeStore = new UptimeStore();
registerAccountStateReset(() => uptimeStore.clear());

export default uptimeStore;
